/**
 * The strip along the bottom of the Desktop: every open window (click to
 * bring it back or minimise it), what's selected, and whether the
 * arrangement has been saved.
 */
import type { ReactNode } from 'react';
import { Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LayoutSaveStatus } from '../../lib/desktop-layout-saver';

export interface TaskbarWindow {
  id: string;
  title: string;
  icon: ReactNode;
  minimized: boolean;
  focused: boolean;
}

function statusText(status: LayoutSaveStatus, loadFailed: boolean): string | null {
  if (status === 'saving' || status === 'pending') return 'Saving arrangement…';
  if (status === 'retrying') return 'Arrangement not saved yet — retrying';
  if (status === 'limit') return 'Too many arranged items — reset the arrangement to keep saving';
  if (loadFailed) return 'Couldn’t load your arrangement — showing the default';
  return null;
}

export function DesktopTaskbar({
  windows,
  onWindow,
  selectionText,
  status,
  loadFailed,
  onResetArrangement,
  onSettings,
}: {
  windows: readonly TaskbarWindow[];
  onWindow: (id: string) => void;
  selectionText: string | null;
  status: LayoutSaveStatus;
  loadFailed: boolean;
  onResetArrangement: () => void;
  /** The Desktop's settings (its style). */
  onSettings?: () => void;
}) {
  const text = statusText(status, loadFailed);
  return (
    <div className="flex min-h-10 shrink-0 flex-wrap items-center gap-2 border-t border-border bg-card/70 px-2 py-1 text-xs">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1" aria-label="Open windows">
        {windows.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => onWindow(w.id)}
            aria-pressed={!w.minimized && w.focused}
            className={cn(
              'flex max-w-[12rem] items-center gap-1.5 rounded border px-2 py-1',
              w.minimized
                ? 'border-dashed border-border text-muted-foreground'
                : w.focused
                  ? 'border-primary/50 bg-primary/10 text-foreground'
                  : 'border-border text-foreground',
            )}
            title={w.minimized ? `Restore ${w.title}` : w.title}
          >
            <span className="shrink-0">{w.icon}</span>
            <span className="truncate">{w.title}</span>
          </button>
        ))}
      </div>
      {selectionText && <span className="text-muted-foreground">{selectionText}</span>}
      {text && (
        <span
          role="status"
          className={cn(
            'text-muted-foreground',
            (status === 'retrying' || status === 'limit') && 'text-amber-700 dark:text-amber-400',
          )}
        >
          {text}
          {status === 'limit' && (
            <button type="button" onClick={onResetArrangement} className="ml-2 underline">
              Reset arrangement
            </button>
          )}
        </span>
      )}
      {onSettings && (
        <button
          type="button"
          onClick={onSettings}
          aria-label="Desktop settings"
          title="Desktop settings"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Settings className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
